import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import type { ForgotPasswordViewModel, ResetPasswordViewModel, UserInfoDto, UserInfoDtoCompany } from '@/ApiClient';
import { filterNumericOnly } from '@lib/formatters';
import { httpClient } from '@lib/http-client';
import * as selectedBrandStorage from '@lib/selected-brand-storage';

// Response of ASP.NET Identity's MapIdentityApi `manage/2fa` endpoint.
type TwoFactorResponse = {
  sharedKey: string;
  recoveryCodesLeft: number;
  recoveryCodes?: string[];
  isTwoFactorEnabled: boolean;
  isMachineRemembered: boolean;
};

type ValidationErrors = Record<string, string[]>;

export const useUser = defineStore('user', {
  state: () => ({
    current: null as UserInfoDto | null,

    // When all global stores have been fetched, this is set to true
    fullyLoaded: false,
  }),

  getters: {
    isAdmin: (state) => {
      return state.current?.roles?.some((r) => r == 'Admin');
    },
    loggedIn: (state) => {
      return !!state.current;
    },
    companies: (state) => {
      return state.current?.companies ?? [];
    },
    company(): UserInfoDtoCompany | null {
      if (!this.current) return null;
      const current = this.companies.find((c) => c.brandID === this.current?.currentBrandId);
      return current ?? this.companies[0] ?? null;
    },
    // Typed as always present because every page past login runs with a brand: app.ts
    // boots a user without one into the no-PLM-access screen instead. Only code that can
    // run before that point (boot, the http client) may see it unset, and checks for it.
    brandId(): number {
      // Storage is only consulted pre-boot, before manage_GetUserData has loaded `current`.
      // Once `current` is loaded, the server's resolved brand is authoritative — falling back
      // to storage after that would let a stale id (e.g. from a previous user on this browser)
      // mask a legitimate "no PLM brand" answer.
      if (!this.current) return selectedBrandStorage.read() as number;
      return (this.current.currentBrandId ?? this.company?.brandID ?? null) as number;
    },
    isSupplier(): boolean {
      if (!this.company) return false;

      return this.company.type !== 1;
    },
    need2fa(): boolean {
      // Non-PLM users have no company/brand, so they can't be subject to a brand 2FA requirement.
      if (!this.company) return false;
      return this.company.brandRequires2fa && !this.current?.isTwoFactorEnabled;
    },
    userName: (state) => {
      return state.current?.userName;
    },
    name: (state) => {
      return state.current?.name;
    },
    photo: (state) => {
      return state.current?.photo;
    },
  },

  actions: {
    async ensureCheckedStatus() {
      if (!this.loggedIn) {
        await this.checkStatus();
      }
    },

    async checkStatus() {
      try {
        this.current = await this.$api.manage_GetUserData();
        if (this.current?.currentBrandId) {
          selectedBrandStorage.write(this.current.currentBrandId);
        }
      } catch {
        this.current = null;
      }
    },
    // The /api/account/generated/* endpoints come from ASP.NET Identity's MapIdentityApi,
    // which is not part of the OpenAPI document the API client is generated from — so these
    // three actions are the one place left that talks to the raw http client.
    async check2fa() {
      const {
        data: { sharedKey, isTwoFactorEnabled },
      } = await httpClient.post<TwoFactorResponse>('/api/account/generated/manage/2fa', {});

      return {
        sharedKey,
        isTwoFactorEnabled,
      };
    },
    async enable2fa(twoFactorCode: string | undefined) {
      try {
        const {
          data: { sharedKey, isTwoFactorEnabled, recoveryCodes },
        } = await httpClient.post<TwoFactorResponse>('/api/account/generated/manage/2fa', {
          enable: true,
          twoFactorCode: filterNumericOnly(twoFactorCode),
        });

        return { sharedKey, isTwoFactorEnabled, recoveryCodes };
      } catch (e) {
        if (isAxiosError<{ errors?: ValidationErrors }>(e)) {
          console.warn('error with enable2fa', e.response?.data);
          if (e.response?.data?.errors) return { errors: e.response.data.errors };
        }

        throw e;
      }
    },
    async reset2faSecret() {
      try {
        const {
          data: { sharedKey, isTwoFactorEnabled, recoveryCodes },
        } = await httpClient.post<TwoFactorResponse>('/api/account/generated/manage/2fa', {
          resetSharedKey: true,
        });

        return { sharedKey, isTwoFactorEnabled, recoveryCodes };
      } catch (e) {
        if (isAxiosError<{ errors?: ValidationErrors }>(e)) {
          console.warn('error with reset2faSecret', e.response?.data);
          if (e.response?.data?.errors) return { errors: e.response.data.errors };
        }

        throw e;
      }
    },

    async forgotPassword(data: ForgotPasswordViewModel) {
      const { status, data: user } = await this.$api.account_ForgotPassword(data);

      return { status, user };
    },

    async resetPassword(data: ResetPasswordViewModel) {
      return await this.$api.account_ResetPassword(data);
    },

    async changeAdminUser({ name, image, password }: { name?: string; image?: File; password?: string }) {
      await this.$api.account_ChangeAdminUser(name, image, password);
      await this.checkStatus();
    },
  },
});
